/**
 * React component for the popular destinations section of the landing page
 *
 * Copyright (c) 2022 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import SectionHeader from '../SectionHeader'
import TallDestinationCard from 'client/shared/js/components/TallDestinationCard'
import CardsGrid from 'client/shared/js/components/CardsGrid'
import Link from 'client/shared/js/components/Link'
import useMediaQuery from 'client/shared/js/hooks/useMediaQuery'
import { getPopularDestinations } from 'client/shared/js/services/explore/api'
import { getCityDestinationUrl } from 'common/configs/routeMap'
import styles from './PopularDestinations.module.scss'

const PopularDestinations = ({ className }) => {
  const [destinations, setDestinations] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const { isTablet } = useMediaQuery()

  useEffect(() => {
    setIsLoading(true)
    getPopularDestinations().then((res) => {
      const destinations = res.data

      if (destinations) {
        setDestinations(destinations)
      }
    }).catch(() => {
      setIsError(true)
    }).finally(() => {
      setIsLoading(false)
    })
  }, [])

  const destinationCards = useMemo(() => {
    const cards = destinations?.map((destination) => {
      const { destinationId, type } = destination
      const gpid = type.substring(5)

      return {
        id: destinationId,
        component: (
          <Link to={getCityDestinationUrl(gpid)} anchor className='reset-style'>
            <TallDestinationCard destination={destination} />
          </Link>
        )
      }
    })

    return isTablet ? cards.slice(0, 3) : cards.slice(0, 4)
  }, [destinations, isTablet])

  // Remove the section altogether when the server responds with an error
  if (isError) {
    return null
  }

  return (
    <section className={cx(className)}>
      <SectionHeader
        title='Popular destinations'
        description='Browse curated attractions, trip plans, and hotels for these amazing cities.' />
      <div className={cx(styles['destination-cards-container'])}>
        <CardsGrid
          cards={destinationCards}
          isLoading={isLoading}
          density='tall-cards' />
      </div>
    </section>
  )
}

PopularDestinations.propTypes = {
  className: PropTypes.string
}

export default PopularDestinations
